import { useState } from "react";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Slider } from "../../ui/slider";
import { Checkbox } from "../../ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { VoiceHint } from "../VoiceHint";
import { X, Plus, Flame, Package, Heart, Ban, Thermometer } from "lucide-react";
import { IconWrapper } from "../IconWrapper";
import { equipmentIcons, spiceLevelText } from "../iconMappings";

interface PreferencesProfile {
  pantry: string[];
  favorites: string[];
  neverAgain: string[];
  heatLevel: number;
  reheating: string[];
}

interface PreferencesPantrySectionProps {
  profile: PreferencesProfile;
  onUpdate: (updates: Partial<PreferencesProfile>) => void;
  currentTier: string;
}

export function PreferencesPantrySection({ 
  profile, 
  onUpdate, 
  currentTier 
}: PreferencesPantrySectionProps) {
  const [newFavorite, setNewFavorite] = useState("");
  const [newNeverAgain, setNewNeverAgain] = useState("");

  const pantryStaples = [
    { id: "olive-oil", label: "Olive Oil", category: "oils" },
    { id: "vegetable-oil", label: "Vegetable Oil", category: "oils" },
    { id: "coconut-oil", label: "Coconut Oil", category: "oils" },
    { id: "salt", label: "Salt", category: "basics" },
    { id: "black-pepper", label: "Black Pepper", category: "basics" },
    { id: "garlic-powder", label: "Garlic Powder", category: "spices" },
    { id: "onion-powder", label: "Onion Powder", category: "spices" },
    { id: "paprika", label: "Paprika", category: "spices" },
    { id: "cumin", label: "Cumin", category: "spices" },
    { id: "oregano", label: "Oregano", category: "herbs" },
    { id: "basil", label: "Basil", category: "herbs" },
    { id: "thyme", label: "Thyme", category: "herbs" },
    { id: "white-rice", label: "White Rice", category: "grains" },
    { id: "brown-rice", label: "Brown Rice", category: "grains" },
    { id: "quinoa", label: "Quinoa", category: "grains" },
    { id: "pasta", label: "Pasta", category: "grains" },
    { id: "flour", label: "All-Purpose Flour", category: "baking" },
    { id: "sugar", label: "Sugar", category: "baking" },
    { id: "baking-powder", label: "Baking Powder", category: "baking" },
    { id: "vanilla", label: "Vanilla Extract", category: "baking" }
  ];

  const reheatingMethods = [
    { id: "microwave", label: "Microwave", icon: equipmentIcons.microwave },
    { id: "oven", label: "Oven", icon: equipmentIcons.oven },
    { id: "stovetop", label: "Stovetop", icon: equipmentIcons.stovetop },
    { id: "toaster-oven", label: "Toaster Oven", icon: equipmentIcons['toaster-oven'] },
    { id: "air-fryer", label: "Air Fryer", icon: equipmentIcons['air-fryer'] }
  ];

  const togglePantryItem = (item: string) => {
    const current = profile.pantry || [];
    if (current.includes(item)) {
      onUpdate({ pantry: current.filter(p => p !== item) });
    } else {
      onUpdate({ pantry: [...current, item] });
    }
  };

  const addToken = (field: keyof PreferencesProfile, value: string) => {
    if (!value.trim()) return;
    const currentArray = profile[field] as string[];
    if (!currentArray.includes(value.trim())) {
      onUpdate({ [field]: [...currentArray, value.trim()] });
    }
  };

  const removeToken = (field: keyof PreferencesProfile, value: string) => {
    const currentArray = profile[field] as string[];
    onUpdate({ [field]: currentArray.filter(item => item !== value) });
  };

  const toggleReheatingMethod = (method: string) => {
    const current = profile.reheating || [];
    if (current.includes(method)) {
      onUpdate({ reheating: current.filter(r => r !== method) });
    } else {
      onUpdate({ reheating: [...current, method] });
    }
  };

  const getHeatLevelLabel = (level: number) => {
    if (level === 0) return "No spice please!";
    if (level === 1) return spiceLevelText[1];
    if (level === 2) return spiceLevelText[2];
    if (level === 3) return spiceLevelText[3];
    if (level === 4) return spiceLevelText[4];
    return spiceLevelText[5];
  };

  return (
    <div className="space-y-8">
      <VoiceHint 
        message="Let's personalize your pantry! Tell me what you love, what you always have on hand, and what you never want to see again."
        voice="medium"
      />

      {/* Pantry Staples */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Package className="h-4 w-4" />
            Pantry Staples
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            What do you typically have in your kitchen?
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {['oils', 'basics', 'spices', 'herbs', 'grains', 'baking'].map((category) => (
              <div key={category}>
                <h4 className="text-sm font-medium mb-2 capitalize text-muted-foreground">
                  {category === 'basics' ? 'Basics' : category}
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {pantryStaples
                    .filter(item => item.category === category)
                    .map((item) => (
                      <button
                        key={item.id}
                        onClick={() => togglePantryItem(item.id)}
                        className={`p-2 text-left border rounded-md text-sm transition-colors ${
                          profile.pantry?.includes(item.id)
                            ? "bg-sage/10 border-sage/20 text-sage"
                            : "hover:bg-muted"
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Favorite Ingredients */}
      <div className="space-y-3">
        <Label className="text-base flex items-center gap-2">
          <Heart className="h-4 w-4 text-red-500" />
          Favorite Ingredients
        </Label>
        <p className="text-sm text-muted-foreground">
          Ingredients you love and want to see more often
        </p>
        <div className="flex gap-2 mb-2">
          <Input
            placeholder="Add favorite ingredient (e.g., avocado, salmon)"
            value={newFavorite}
            onChange={(e) => setNewFavorite(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addToken('favorites', newFavorite);
                setNewFavorite("");
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              addToken('favorites', newFavorite);
              setNewFavorite("");
            }}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {profile.favorites?.map((favorite) => (
            <Badge
              key={favorite}
              variant="outline"
              className="bg-green-50 text-green-700 border-green-200"
            >
              {favorite}
              <button
                onClick={() => removeToken('favorites', favorite)}
                className="ml-1 hover:text-green-900"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      </div>

      {/* Never Again Ingredients */}
      <div className="space-y-3">
        <Label className="text-base flex items-center gap-2">
          <Ban className="h-4 w-4 text-red-500" />
          "Never Again" Ingredients
        </Label>
        <p className="text-sm text-muted-foreground">
          Ingredients you've tried and really don't like
        </p>
        <div className="flex gap-2 mb-2">
          <Input
            placeholder="Add ingredient to avoid (e.g., liver, blue cheese)"
            value={newNeverAgain}
            onChange={(e) => setNewNeverAgain(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addToken('neverAgain', newNeverAgain);
                setNewNeverAgain("");
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              addToken('neverAgain', newNeverAgain);
              setNewNeverAgain("");
            }}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {profile.neverAgain?.map((ingredient) => (
            <Badge
              key={ingredient}
              variant="outline"
              className="bg-red-50 text-red-700 border-red-200"
            >
              {ingredient}
              <button
                onClick={() => removeToken('neverAgain', ingredient)}
                className="ml-1 hover:text-red-900"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      </div>

      {/* Heat Tolerance */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Flame className="h-4 w-4" />
            Heat Tolerance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Slider
              value={[profile.heatLevel]}
              onValueChange={(value) => onUpdate({ heatLevel: value[0] })}
              max={5}
              step={1}
              className="w-full"
            />
            <div className="text-center">
              <div className="text-sm font-medium">
                {getHeatLevelLabel(profile.heatLevel)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Level {profile.heatLevel}/5
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reheating Methods */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Reheating Methods Available</CardTitle>
          <p className="text-sm text-muted-foreground">
            How do you typically reheat leftovers?
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {reheatingMethods.map((method) => (
              <div
                key={method.id}
                className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                  profile.reheating?.includes(method.id)
                    ? "bg-sage/10 border-sage/20"
                    : "hover:bg-muted"
                }`}
                onClick={() => toggleReheatingMethod(method.id)}
              >
                <Checkbox
                  checked={profile.reheating?.includes(method.id)}
                  onChange={() => {}} // Handled by div click
                />
                <div className="flex items-center gap-2">
                  <IconWrapper icon={method.icon} />
                  <Label className="text-sm cursor-pointer">
                    {method.label}
                  </Label>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Personalization Hint */}
      {(profile.favorites?.length > 0 || profile.neverAgain?.length > 0) && (
        <VoiceHint 
          message="Perfect! I love getting to know your taste preferences. I'll make sure to feature your favorites and keep those 'never again' ingredients far away! 🎯"
          voice="medium"
        />
      )}
    </div>
  );
}